import { Box, ButtonGroup, IconButton, Popover, Tooltip } from "@mui/material";
import { useRef } from "react";
import { ChevronDown } from "../../styles/icons/chevron-down";

import type { IconButtonProps } from "@mui/material";
import type { FC, PropsWithChildren } from "react";

interface ButtonMenuProps extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  button: (props: IconButtonProps & { onClick?: () => void }) => React.JSX.Element;
  disabled?: boolean;
  title?: string;
}

export const ButtonMenu: FC<ButtonMenuProps> = ({
  open,
  onClose,
  onOpen,
  button,
  disabled,
  children,
  title,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = (): void => {
    if (open) onClose();
    else onOpen();
  };

  const handleClose = (event: Event): void => {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    onClose();
  };

  return (
    <Box ref={anchorRef} sx={{ display: "inline-block" }}>
      <ButtonGroup
        aria-label="outlined primary button group"
        sx={{
          ":hover": {
            backgroundColor: "background.active",
          },
        }}
      >
        {button({
          disabled,
          sx: { pr: 0.5, borderTopRightRadius: 0, borderBottomRightRadius: 0 },
        })}
        <Tooltip title={title}>
          <span>
            <IconButton
              data-analytics-id="list-button-menu-open"
              disabled={disabled}
              tabIndex={-1}
              onClick={handleToggle}
              size="small"
              sx={{
                pl: 0.2,
                pr: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <ChevronDown />
            </IconButton>
          </span>
        </Tooltip>
      </ButtonGroup>
      <Popover
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        onClose={handleClose}
        open={open}
        slotProps={{ paper: { style: { minWidth: 100, padding: 5 } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>{children}</Box>
      </Popover>
    </Box>
  );
};
